import { Spinner } from '@design-system';
import { QuestionnaireIntroScreen } from '@/features/advisor-onboarding/questionnaire/components/questionnaire-intro-screen';
import { QuestionnaireFinalScreen } from '@/features/advisor-onboarding/questionnaire/components/questionnaire-final-screen';
import { QuestionScreen } from '@/features/advisor-onboarding/questionnaire/components/question-screen';

import { useCallback, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuestionnaire } from '@/features/advisor-onboarding/questionnaire/hooks/use-questionnaire';
import { useQuestionnaireNavigation } from '@/features/advisor-onboarding/questionnaire/hooks/use-questionnaire-navigation';

import { analytics } from '@client/analytics';
import { QUESTIONS_BY_ID, type QuestionId, type QuestionnaireAnswers } from '@shared';

interface AdvisorOnboardingQuestionnaireStepProps {
  advisorId: string;
}

export function AdvisorOnboardingQuestionnaireStep({
  advisorId,
}: AdvisorOnboardingQuestionnaireStepProps) {
  const {
    isLoading,
    isSubmitting,
    responses,
    orgType,
    organizationName,
    advisorFirstName,
    visibleQuestions,
    nextUnanswered,
    isComplete,
    isQuestionVisible,
    getProgress,
    submitAnswer,
    isEdjPilotAdvisor,
    isDistributionPartnersRestricted,
  } = useQuestionnaire({ advisorId });

  const navigation = useQuestionnaireNavigation({
    advisorId,
    visibleQuestions,
    orgType,
    organizationName,
    isEdjPilotAdvisor,
    isDistributionPartnersRestricted,
  });

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        index
        element={
          <QuestionnaireIntroOrResume
            advisorId={advisorId}
            hasStarted={Object.keys(responses).length > 0}
            nextUnansweredId={nextUnanswered?.id ?? null}
            isComplete={isComplete}
            navigation={navigation}
          />
        }
      />
      <Route
        path="final"
        element={
          <QuestionnaireFinalScreen
            advisorId={advisorId}
            firstName={advisorFirstName}
            userIntent={responses.user_intent ?? null}
            partnerInterest={responses.bga_partner_interest ?? null}
            onStartFreeTrial={navigation.goToActivateSeat}
          />
        }
      />
      <Route
        path=":questionId"
        element={
          <QuestionRoute
            responses={responses}
            orgType={orgType}
            organizationName={organizationName}
            isSubmitting={isSubmitting}
            visibleQuestionIds={visibleQuestions.map(q => q.id)}
            isQuestionVisible={isQuestionVisible}
            getProgress={getProgress}
            onSubmit={submitAnswer}
            onAdvance={(questionId, answersForBranching) => {
              navigation.goNextFrom(questionId, answersForBranching);
            }}
            onBack={questionId => navigation.goPrevFrom(questionId)}
          />
        }
      />
    </Routes>
  );
}

interface QuestionnaireIntroOrResumeProps {
  advisorId: string;
  hasStarted: boolean;
  nextUnansweredId: QuestionId | null;
  isComplete: boolean;
  navigation: ReturnType<typeof useQuestionnaireNavigation>;
}

function QuestionnaireIntroOrResume({
  hasStarted,
  nextUnansweredId,
  isComplete,
  navigation,
}: QuestionnaireIntroOrResumeProps) {
  useEffect(() => {
    if (isComplete) {
      navigation.goToFinal();
    } else if (hasStarted && nextUnansweredId) {
      navigation.goToQuestion(nextUnansweredId);
    }
  }, [isComplete, hasStarted, nextUnansweredId, navigation]);

  const handleStart = useCallback(() => {
    analytics.track('advisor_onboarding_questionnaire_intro_started', {}, 'engagement');

    if (nextUnansweredId) {
      navigation.goToQuestion(nextUnansweredId);
    }
  }, [nextUnansweredId, navigation]);

  if (hasStarted || isComplete) {
    return null;
  }

  return <QuestionnaireIntroScreen onStart={handleStart} />;
}

interface QuestionRouteProps {
  responses: QuestionnaireAnswers;
  orgType: import('@shared').OrgType | null;
  organizationName: string | null;
  isSubmitting: boolean;
  visibleQuestionIds: QuestionId[];
  isQuestionVisible: (id: QuestionId) => boolean;
  getProgress: (id: QuestionId) => { current: number; estimatedTotal: number };
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<unknown>;
  onAdvance: (currentId: QuestionId, answers: QuestionnaireAnswers) => void;
  onBack: (currentId: QuestionId) => void;
}

function QuestionRoute({
  responses,
  orgType,
  organizationName,
  isSubmitting,
  isQuestionVisible,
  getProgress,
  onSubmit,
  onAdvance,
  onBack,
}: QuestionRouteProps) {
  const { questionId } = useParams<{ questionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const question = questionId ? QUESTIONS_BY_ID[questionId as QuestionId] : undefined;

  useEffect(() => {
    if (questionId && question && !isQuestionVisible(questionId as QuestionId)) {
      navigate(location.pathname.replace(/\/[^/]+$/, ''));
    }
  }, [question, questionId, isQuestionVisible, navigate, location.pathname]);

  if (!question) {
    return null;
  }

  const progress = getProgress(question.id);

  const handleSubmit = async (patch: Partial<QuestionnaireAnswers>) => {
    await onSubmit(patch);
  };

  const handleAdvance = (patch: Partial<QuestionnaireAnswers> = {}) => {
    onAdvance(question.id, { ...responses, ...patch });
  };

  const handleBack = () => onBack(question.id);

  return (
    <QuestionScreen
      key={question.id}
      question={question}
      answers={responses}
      orgType={orgType}
      organizationName={organizationName}
      progressCurrent={progress.current}
      progressEstimatedTotal={progress.estimatedTotal}
      isSubmitting={isSubmitting}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onAdvance={handleAdvance}
    />
  );
}
